import { useState, useEffect, useCallback } from 'react'
import { Avatar } from '@campgladiator/cgui-core.atoms.avatar'
import { Paragraph } from '@campgladiator/cgui-core.atoms.typography'
import { ProgressCard, BannerNotice } from '..'
import {
  BREAKPOINTS,
  calculateStatDifferences,
  config,
  findMostRecentSubmission,
  formatDateDDMM,
} from '../../../common/utility'

const statsInitialState = {
  bodyWeight: null,
  bodyFat: null,
  muscleMass: null,
  mileTime: null,
  pushUps: null,
  sitUps: null,
  squats: null,
}

const Progress = ({ challenge, user, isFitChallengeActive = false }) => {
  const [primaryStats, setPrimaryStats] = useState(statsInitialState)
  const [secondaryStats, setSecondaryStats] = useState(statsInitialState)

  const calculatePrimaryStats = useCallback((cgFitMetricsList) => {
    if (!cgFitMetricsList?.length) {
      return
    }
    const mostRecentStats = findMostRecentSubmission(cgFitMetricsList)
    setPrimaryStats(mostRecentStats)
  }, [])

  const calculateSecondaryStats = useCallback((cgFitMetricsList) => {
    if (!cgFitMetricsList || cgFitMetricsList?.length < 2) {
      return
    }
    const differences = calculateStatDifferences(cgFitMetricsList)
    setSecondaryStats(differences)
  }, [])

  useEffect(() => {
    calculatePrimaryStats(challenge?.cgFitMetricsList)
    calculateSecondaryStats(challenge?.cgFitMetricsList)
  }, [
    calculateSecondaryStats,
    calculatePrimaryStats,
    challenge?.cgFitMetricsList,
  ])

  const FitChallengeActiveContent = () => (
    <>
      <Paragraph weight="bold" className="progress__pf-name">
        Let's do this, {user?.firstName}!
      </Paragraph>
      <Paragraph weight="book" className="progress__ch-details">
        The challenge is ON! Submit your stats for {challenge?.description}{' '}
        between{' '}
        <strong>
          {formatDateDDMM(challenge?.startDate)} -{' '}
          {formatDateDDMM(challenge?.endDate)}
        </strong>{' '}
        to show off and celebrate your progress.
      </Paragraph>
    </>
  )

  const FitChallengeInactiveContent = () => (
    <>
      <Paragraph weight="bold" className="progress__pf-name">
        Hi {user?.firstName}!
      </Paragraph>
      <Paragraph weight="book" className="progress__ch-details">
        While there are no active CG Fit challenges right now, any of your
        existing challenge progress will be available for you to review below.
      </Paragraph>
    </>
  )

  return (
    <div className="progress">
      <div className="progress__profile">
        <div className="progress__profile-img">
          <Avatar
            size="100%"
            src={user?.image_url ?? config.REACT_APP_PLACEHOLDER_IMG_URL}
          />
        </div>
        <div className="progress__pf-details">
          {isFitChallengeActive ? (
            <FitChallengeActiveContent />
          ) : (
            <FitChallengeInactiveContent />
          )}
        </div>
      </div>
      {!!challenge && <BannerNotice />}
      {isFitChallengeActive && (
        <div
          className="progress__cards"
          style={{
            marginTop:
              !!challenge && window.innerWidth >= BREAKPOINTS.TABLET_LANDSCAPE
                ? '15px'
                : '',
          }}
        >
          <ProgressCard
            primaryValue={primaryStats.bodyWeight}
            secondaryValue={secondaryStats.bodyWeight}
            title="weight"
          />
          <ProgressCard
            primaryValue={
              primaryStats.bodyFat ? `${primaryStats.bodyFat}%` : '--'
            }
            secondaryValue={secondaryStats.bodyFat}
            title="body fat %"
          />
          <ProgressCard
            primaryValue={primaryStats.muscleMass}
            secondaryValue={secondaryStats.muscleMass}
            title="muscle mass"
          />
          <ProgressCard
            primaryValue={primaryStats.mileTime}
            secondaryValue={secondaryStats.mileTime}
            title="mile time"
          />
          <ProgressCard
            primaryValue={primaryStats.pushUps}
            secondaryValue={secondaryStats.pushUps}
            title="push ups"
          />
          <ProgressCard
            primaryValue={primaryStats.sitUps}
            secondaryValue={secondaryStats.sitUps}
            title="sit ups"
          />
          <ProgressCard
            primaryValue={primaryStats.squats}
            secondaryValue={secondaryStats.squats}
            title="squats"
          />
        </div>
      )}
    </div>
  )
}

export default Progress
